import React from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "@/context/LanguageContext";
import { Reveal, Kicker } from "@/components/Reveal";
import { images } from "@/data/content";
import ProjectsGallery from "@/components/ProjectsGallery";
import Testimonials from "@/components/Testimonials";

const Home = () => {
  const { t } = useLanguage();

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-[100vh] flex flex-col justify-end overflow-hidden">
        <img
          src={images.hero}
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-charcoal/55 via-charcoal/15 to-transparent" />
        <div className="relative max-w-7xl mx-auto w-full px-6 md:px-12 pb-20 md:pb-28">
          <Reveal>
            <Kicker className="text-cream/90">{t.hero.kicker}</Kicker>
          </Reveal>
          <Reveal delay={0.1}>
            <h1 className="font-serif font-light text-cream text-5xl sm:text-6xl md:text-7xl lg:text-8xl leading-[1.02] tracking-tight mt-6 max-w-5xl">
              {t.hero.headline}
            </h1>
          </Reveal>
          <Reveal delay={0.25}>
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mt-10">
              <p className="text-cream/85 text-lg md:text-xl max-w-md font-light leading-relaxed">
                {t.hero.description}
              </p>
              <Link
                to="/contact"
                data-testid="hero-cta"
                className="self-start text-xs uppercase tracking-[0.18em] text-charcoal bg-cream hover:bg-sage hover:text-cream transition-colors duration-300 px-9 py-4"
              >
                {t.hero.cta}
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* BRAND MARK BAND */}
      <section className="bg-cream pt-20 md:pt-28 pb-4" data-testid="home-brandmark">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex justify-center">
          <Reveal>
            <img
              src={images.logo}
              alt="BY VAYA"
              className="w-full max-w-xs md:max-w-sm h-auto object-contain"
            />
          </Reveal>
        </div>
      </section>

      {/* ABOUT */}
      <section className="bg-cream py-24 md:py-36" data-testid="home-about">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16">
            <div className="md:col-span-3">
              <Reveal>
                <Kicker>{t.about.kicker}</Kicker>
              </Reveal>
            </div>
            <div className="md:col-span-9">
              <Reveal>
                <p className="font-serif font-light text-3xl sm:text-4xl md:text-5xl leading-[1.15] text-charcoal">
                  {t.about.lead}
                </p>
              </Reveal>
              <Reveal delay={0.15}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-10 mt-14">
                  <p className="text-charcoal/70 leading-relaxed font-light">
                    {t.about.body}
                  </p>
                  <div className="flex flex-col gap-6">
                    <p className="font-serif text-2xl text-olive leading-snug">
                      {t.about.experience}
                    </p>
                    <Link
                      to="/about"
                      data-testid="home-about-link"
                      className="self-start text-xs uppercase tracking-[0.18em] text-olive border-b border-olive pb-1 hover:text-charcoal hover:border-charcoal transition-colors"
                    >
                      {t.about.link}
                    </Link>
                  </div>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="bg-cream-2 py-24 md:py-36" data-testid="home-services">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-16">
            <div>
              <Reveal>
                <Kicker>{t.services.kicker}</Kicker>
              </Reveal>
              <Reveal delay={0.1}>
                <h2 className="font-serif font-light text-5xl sm:text-6xl mt-5 text-charcoal">
                  {t.services.title}
                </h2>
              </Reveal>
            </div>
            <Reveal delay={0.15}>
              <p className="text-charcoal/70 max-w-sm font-light leading-relaxed">
                {t.services.intro}
              </p>
            </Reveal>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-border-subtle border border-border-subtle">
            {t.services.buckets.map((b, i) => (
              <Reveal key={b.title} delay={i * 0.05}>
                <Link
                  to="/services"
                  data-testid={`home-service-${i}`}
                  className="group block bg-cream-2 hover:bg-cream transition-colors duration-300 p-8 md:p-10 h-full"
                >
                  <div className="flex items-baseline justify-between">
                    <span className="font-mono text-xs text-taupe">{b.n}</span>
                    <span className="text-taupe group-hover:text-olive group-hover:translate-x-1 transition-all duration-300">
                      →
                    </span>
                  </div>
                  <h3 className="font-serif text-4xl md:text-5xl text-charcoal mt-5 group-hover:text-sage transition-colors duration-300">
                    {b.title}
                  </h3>
                  <p className="text-charcoal/60 font-light mt-3 text-sm leading-relaxed">
                    {b.desc}
                  </p>
                  <p className="font-mono text-[0.7rem] uppercase tracking-[0.16em] text-olive/80 mt-5">
                    {b.items.join("  ·  ")}
                  </p>
                </Link>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.1}>
            <div className="mt-12">
              <Link
                to="/services"
                data-testid="home-services-link"
                className="text-xs uppercase tracking-[0.18em] text-olive border-b border-olive pb-1 hover:text-charcoal hover:border-charcoal transition-colors"
              >
                {t.services.cta}
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* SELECTED PROJECTS */}
      <section className="bg-cream py-24 md:py-36" data-testid="home-projects">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="mb-16">
            <Reveal>
              <Kicker>{t.projects.kicker}</Kicker>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="font-serif font-light text-5xl sm:text-6xl mt-5 text-charcoal">
                {t.projects.title}
              </h2>
            </Reveal>
          </div>

          <ProjectsGallery limit={4} testIdPrefix="home-project" />

          <Reveal delay={0.1}>
            <div className="mt-20 text-center">
              <Link
                to="/projects"
                data-testid="home-projects-link"
                className="text-xs uppercase tracking-[0.18em] text-olive border-b border-olive pb-1 hover:text-charcoal hover:border-charcoal transition-colors"
              >
                {t.projects.cta}
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* PROCESS */}
      <section className="bg-olive text-cream py-24 md:py-36" data-testid="home-process">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="mb-16">
            <Reveal>
              <Kicker>{t.process.kicker}</Kicker>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="font-serif font-light text-5xl sm:text-6xl mt-5">
                {t.process.title}
              </h2>
            </Reveal>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-px bg-cream/15 border border-cream/15">
            {t.process.steps.map((step, i) => (
              <Reveal key={step.no} delay={i * 0.08}>
                <div
                  className="bg-olive p-8 md:p-10 h-full min-h-[260px] flex flex-col justify-between"
                  data-testid={`process-step-${i}`}
                >
                  <span className="font-mono text-xs tracking-[0.2em] text-sage">
                    {step.no}
                  </span>
                  <div>
                    <h3 className="font-serif text-3xl md:text-4xl mb-4">
                      {step.title}
                    </h3>
                    <p className="text-cream/70 font-light leading-relaxed text-sm">
                      {step.desc}
                    </p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <Testimonials />

      {/* CONTACT CTA */}
      <section className="bg-cream py-28 md:py-40" data-testid="home-contact-cta">
        <div className="max-w-6xl mx-auto px-6 md:px-12 text-center">
          <Reveal>
            <Kicker>{t.contact.kicker}</Kicker>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-serif font-light text-4xl sm:text-6xl md:text-7xl leading-[1.05] mt-6 text-charcoal max-w-4xl mx-auto">
              {t.contact.title}
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <Link
              to="/contact"
              data-testid="home-contact-link"
              className="inline-block mt-12 text-xs uppercase tracking-[0.18em] text-cream bg-olive hover:bg-charcoal transition-colors duration-300 px-10 py-5"
            >
              {t.contact.subtitle}
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Home;
